import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Button from '@carrier-io/air-react/Button';
import Stack from '@carrier-io/air-react/Stack';
import { ConnectionContextType } from '../Connection';
import NextArrow from 'components/icons/NextArrow';
import ButtonBase from '@carrier-io/air-react/ButtonBase';
import Typography from '@carrier-io/air-react/Typography';
import DragDropUpload, { FileItem } from '@carrier-io/air-react/patterns/Upload/DragDropUpload';
import UploadedFile from '@carrier-io/air-react/patterns/UploadedFile/UploadedFile';
import Divider from '@carrier-io/air-react/Divider';
import CertificateStatus from './CertificateStatus';
import { Align, TypographyColor, Variant } from 'constants/componentEnums';
import { useNotification } from 'context/NotificationContext';
import { NotificationBgColor } from 'store/models/Notification';
import { isProjectOngoing, isUploadingDisabled, ProjectOnboardingStatus } from 'store/models/Project';
import { ALLOWED_FILE_EXTENSIONS, CERTIFICATE_ERROR_MESSAGES } from 'store/models/Connection';
import { UPLOAD_CERTIFICATE } from 'store/graphql/mutations/UPLOAD_CERTIFICATE';
import styles from '../../../styles/Connection.module.css';

const AddCertificate = () => {
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fileExtension, setFileExtension] = useState('');
  const [certificateData, setCertificateData] = useState<ArrayBuffer | string>('');
  const [certificateFile, setCertificateFile] = useState<FileItem[]>([]);
  const [isCertificateLoading, setIsCertificateLoading] = useState(false);
  const [showUploadedFile, setShowUploadedFile] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCertificateInvalid, setIsCertificateInvalid] = useState(false);
  const { cancelConfigure, handleChange, refetchVendorData, vendorData } = useOutletContext<ConnectionContextType>();
  const { projectId = '' } = useParams();
  const formattedProjectId = projectId?.toLocaleLowerCase();
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const currentProject = vendorData?.projects?.find((project) => project.id === formattedProjectId);
  const projectStatus: ProjectOnboardingStatus = currentProject?.status ?? ProjectOnboardingStatus.DRAFT;
  const isCertificateAlreadyUploaded = isProjectOngoing(projectStatus);
  const disableUpload = isUploadingDisabled(projectStatus);

  const [uploadCertificate, { loading }] = useMutation(UPLOAD_CERTIFICATE);

  const getFileNameAndExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return { name: fileName, extension: '' };
    }

    return {
      name: fileName.slice(0, lastDotIndex).toLocaleLowerCase(),
      extension: fileName.slice(lastDotIndex + 1).toLocaleLowerCase(),
    };
  };

  const handleDeleteFile = () => {
    setShowUploadedFile(false);
    setCertificateFile([]);
    setIsCertificateInvalid(false);
    setErrorMessage('');
    setIsSuccess(false);
  };

  const handleCertificateUpload = (e: FileItem[]) => {
    setShowUploadedFile(true);
    setIsCertificateLoading(true);
    setIsCertificateInvalid(false);

    if (!e[0]?.fileData) {
      return;
    }

    const { name, extension } = getFileNameAndExtension(e[0].fileData?.name || '');

    if (!ALLOWED_FILE_EXTENSIONS.includes(`.${extension}`)) {
      setErrorMessage(CERTIFICATE_ERROR_MESSAGES.invalid);
      setIsCertificateInvalid(true);
    }

    setFileName(name);
    setFileExtension(extension);

    const reader = new FileReader();

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = (event.loaded / event.total) * 100;
        setProgress(percent);
      }
    };
    reader.onload = () => {
      const resultData = (reader?.result as string)?.split(',')[1] || '';
      setCertificateData(resultData);
      setProgress(100);
      setIsCertificateLoading(false);
      setIsSuccess(true);
    };

    reader.onerror = () => {
      setIsCertificateLoading(false);
      setIsSuccess(false);
    };

    reader.readAsDataURL(e[0]?.fileData);
    setCertificateFile(e);
  };

  const handleUploadCertificate = async () => {
    const certificateToUpload = {
      dataProducerName: currentProject?.name,
      vendorId: vendorData?.vendorId,
      projectId: formattedProjectId,
      file: certificateData,
    };

    const isInvalid = Object.values(certificateToUpload).some((item) => !item);

    if (isInvalid) {
      setTextContent(`Invalid data provided`);
      setNotificationBgColor(NotificationBgColor.WARNING);
      setIsNotificationOpened(true);
      return;
    }

    try {
      await uploadCertificate({
        variables: certificateToUpload,
      });
      setTextContent(`Certificate has been successfully uploaded`);
      setNotificationBgColor(NotificationBgColor.SUCCESS);
      setIsNotificationOpened(true);
      refetchVendorData();
      handleChange(0, 1);
    } catch {
      setTextContent('There was an issue with your request');
      setNotificationBgColor(NotificationBgColor.ERROR);
      setIsNotificationOpened(true);
      setErrorMessage(CERTIFICATE_ERROR_MESSAGES.invalidCertificate);
      setIsCertificateInvalid(true);
    }
  };

  const handleSubmit = () => {
    if (isCertificateAlreadyUploaded) {
      handleChange(0, 1);
    } else {
      handleUploadCertificate();
    }
  };

  useEffect(() => {
    if (isCertificateAlreadyUploaded) {
      setIsSuccess(true);
      setIsCertificateInvalid(false);
    }
  }, [isCertificateAlreadyUploaded]);

  return (
    <Stack direction="column" spacing={2} width="80%">
      <Typography
        color={TypographyColor.Text.Primary}
        sx={{
          marginTop: '20px',
          marginBottom: '20px',
        }}
        variant={Variant.H4}
        align={Align.Left}
      >
        Add Your Certificate
      </Typography>
      <Divider variant="full" />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H5} align={Align.Left}>
        Instructions
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        In order to connect data producer to Carrio.io platofrm, it is required to upload CA ceritifcate for AWS IoT JIP
        https://docs.aws.amazon.com/iot/latest/developerguide/jit-provisioning.html. If CA certificate is already
        available, please upload it in the `Upload Your Certificate` section below. Otherwise, please use instruction
        from the following section to generate CA certificate.
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H5} align={Align.Left}>
        Generate Your Certificate
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Please ensure that{' '}
        <a href="https://openssl-library.org/source/" target="_blank">
          OpenSSL
        </a>{' '}
        tool is installed on your PC. Please use the following commands to generate CA key:
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        # set name for your organization <br />
        CANAME=MyOrg-RootCA <br />
        <br />
        # generate encrypted private CA key <br />
        openssl genrsa -out $CANAME.key 2048
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Once commands are executed, there should be a new file generated with a name <b>$CANAME.key</b>, where{' '}
        <b>$CANAME</b> is your org name provided during the command run. Next step will be to extract public CA
        certificate that should be uploaded into Carrier.IO. After execution of the following command file{' '}
        <b>$CANAME.pem</b> should appear. Please use this file to upload in the `Upload Your Certificate` section below.
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        # create CA certificate, 1826 days = 5 years <br />
        # provide common name, country etc. via command: <br />
        openssl req -x509 -new -nodes -key $CANAME.key -sha256 -days 1826 -out $CANAME.pem -subj '/CN=MyOrg Root
        CA/C=AT/ST=Vienna/L=Vienna/O=MyOrg'
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        In order to generate child certificate for you connection/payload testing, please use the following commmands:
      </Typography>
      <Typography
        className={styles.configField}
        color={TypographyColor.Text.Primary}
        variant={Variant.Body2}
        align={Align.Left}
      >
        # create device/child key, 365 days = 1 year <br />
        openssl genrsa -out deviceCert.key 2048 <br />
        # CAUTION: this step will ask for common name, please save it, as it will be required in the next step. <br />
        <br />
        # extract device/child certificate <br />
        openssl req -new -key deviceCert.key -out deviceCert.csr <br />
        <br />
        # sign child certificate with CA key <br />
        openssl x509 -req -in deviceCert.csr -CA $CANAME.pem -CAkey $CANAME.key -CAcreateserial -out deviceCert.crt
        -days 365 -sha256 <br />
        <br />
        # put child and CA ceritificate into single file. <br />
        cat deviceCert.crt $CANAME.pem &gt; deviceCertAndCACert.crt
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        For other details of CA certificate generation see{' '}
        <a href="https://docs.aws.amazon.com/iot/latest/developerguide/manage-your-CA-certs.html" target="_balnk">
          here
        </a>{' '}
        .
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H5} align={Align.Left}>
        Upload Your Certificate
      </Typography>
      {!showUploadedFile && !isCertificateAlreadyUploaded && !disableUpload && (
        <Stack className={styles.uploadedWrapper} direction="column" spacing={2} width="100%">
          <DragDropUpload
            acceptType={ALLOWED_FILE_EXTENSIONS}
            fileItems={certificateFile}
            description="Click or drag file to this area to upload"
            itemsToDisplay={0}
            showProgressBar={false}
            listPlacement="bottom-start"
            onUpload={handleCertificateUpload}
            size="100%"
            acceptedText={'or browse files on your computer only'}
            error={false}
            multiple={false}
          />
        </Stack>
      )}
      {showUploadedFile && (
        <Stack data-testid="uploaded-file" className={styles.progressBar} direction="column" spacing={2} width="100%">
          <UploadedFile
            extension={fileExtension}
            fileName={fileName}
            fileNameSxProps={{
              minWidth: 'fit-content',
            }}
            isLoading={isCertificateLoading}
            isSuccess={isSuccess}
            isInvalid={isCertificateInvalid}
            onDelete={handleDeleteFile}
            progress={progress}
          />
        </Stack>
      )}
      {isCertificateInvalid && (
        <Typography
          data-testid="certificate-error"
          color={TypographyColor.Error.Main}
          variant={Variant.HelperText}
          align={Align.Center}
        >
          {errorMessage}
        </Typography>
      )}
      {(isCertificateAlreadyUploaded || disableUpload) && (
        <CertificateStatus data-testid="certificate-status" status={projectStatus} />
      )}
      <Divider variant="full" />
      <Stack direction="row" spacing={2} alignSelf="flex-end">
        <ButtonBase data-testid="step-cancel" onClick={cancelConfigure}>
          <Typography color="primary">Cancel</Typography>
        </ButtonBase>
        <Button
          data-testid="certificate-upload-submit"
          disabled={!isSuccess || isCertificateInvalid || loading}
          endIcon={<NextArrow />}
          color="primary"
          fullWidth={false}
          onClick={handleSubmit}
        >
          Next: Establish Your Connection
        </Button>
      </Stack>
    </Stack>
  );
};

export default AddCertificate;
